import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent, TableColumn, PaginationConfig } from '../../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { AuthApiService, CatalogueApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { SftpUploadComponent } from '../../../shared/components/contribution-steps/upload/sftp-upload/sftp-upload.component';
import { ToasterService } from '../../../services/toaster.service';

export interface AssetRequestData {
  assetName: string;
  type: string;
  permission: string;
  assetStatus: string;
  requestStatus: string;
  requestedOn: string;
  action: string;
  originalData?: any;
}

@Component({
  selector: 'app-sftp-requests',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ReactiveFormsModule,
    ModalComponent,
    SftpUploadComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './sftp-requests.component.html',
  styleUrl: './sftp-requests.component.scss',
})
export class SftpRequestsComponent implements OnInit, AfterViewInit {
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('permissionCell', { static: false }) permissionCellTemplate!: TemplateRef<any>;

  constructor(
    private authApiService: AuthApiService,
    private catalogueApiService: CatalogueApiService,
    private formatService: FormatService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private toasterService: ToasterService
  ) {}

  loading = false;
  emptyMessage = 'No SFTP requests found';
  requestsData: AssetRequestData[] = [];
  openDropdownId: string | null = null;
  isViewModalOpen = false;
  viewSftpForm!: FormGroup;

  columns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: false },
    { key: 'type', label: 'Asset Type', sortable: false },
    { key: 'permission', label: 'Permission', sortable: false },
    { key: 'assetStatus', label: 'Asset Status', sortable: false },
    { key: 'requestStatus', label: 'Request Status', sortable: false },
    { key: 'requestedOn', label: 'Requested On', sortable: false },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  @ViewChild('requestStatusCell', { static: false }) requestStatusCellTemplate!: TemplateRef<any>;
  @ViewChild('assetStatusCell', { static: false }) assetStatusCellTemplate!: TemplateRef<any>;

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };

  ngOnInit(): void {
    this.viewSftpForm = this.fb.group({
      sftpHost: [''],
      port: [''],
      username: [''],
      password: [''],
      remotePath: [''],
      localFilePath: [''],
      optionalNotes: [''],
    });
    this.loadAssetRequests();
  }

  async loadAssetRequests(): Promise<void> {
    console.log('loadAssetRequests (SFTP): START - setting loading = true');
    this.loading = true;
    try {
      console.log('loadAssetRequests (SFTP): Calling getAssetRequests API');
      const response = await this.authApiService.getAssetRequests(
        this.pagination.currentPage,
        this.pagination.pageSize,
        'SFTP'
      );
      console.log('loadAssetRequests (SFTP): API response received:', response);

      let mappedData: AssetRequestData[] = [];

      // Unwrap response (support both { result, paginationInfo } and { data: { result, paginationInfo } })
      const payload = response && typeof response === 'object' ? (response['data'] ?? response) : null;
      const resultArray = payload?.['result'];
      const paginationInfo = payload?.['paginationInfo'];

      // API was called with type=SFTP, so result already contains only SFTP requests – no client-side filter
      if (resultArray && Array.isArray(resultArray)) {
        const requestsToProcess = resultArray;
        
        console.log('loadAssetRequests (SFTP): Requests to process:', requestsToProcess.length);
        
        if (requestsToProcess.length > 0) {
          console.log('First SFTP request item structure:', JSON.stringify(requestsToProcess[0], null, 2));
        }
        
        // Fetch asset details for each request
        // Use Promise.allSettled to continue even if some asset fetches fail
        const requestsWithDetailsPromises = requestsToProcess.map(async (item: any) => {
            // Extract assetId: API may nest it under assetRequest (iudx/v2/cat/item?id=assetId is called per request)
            const req = item.assetRequest || item;
            const assetId =
              req.assetId || req.asset_id || item.assetId || item.asset_id
              || req.resourceId || req.resource_id || item.resourceId || item.resource_id
              || req.itemId || req.item_id || item.itemId || item.item_id
              || req.id || item.id || item.resource?.id;

            let assetDetails: any = null;

            if (assetId) {
              try {
                const assetResponse = await this.catalogueApiService.getItemById(assetId);
                let rawAssetDetails = assetResponse?.['result'] || assetResponse || null;
                if (Array.isArray(rawAssetDetails) && rawAssetDetails.length > 0) {
                  assetDetails = rawAssetDetails[0];
                } else if (rawAssetDetails && typeof rawAssetDetails === 'object') {
                  assetDetails = rawAssetDetails;
                } else {
                  assetDetails = null;
                }
              } catch (error: any) {
                if (error?.status !== 404) {
                  console.error(`Error fetching asset details for ID ${assetId}:`, error);
                }
                assetDetails = null;
              }
            }

            // Map fields: prefer item API (assetDetails), then top-level / assetRequest from response
            const assetName = assetDetails?.name || assetDetails?.label || item.itemName || item.assetName || item.name || item.label || 'N/A';

            let type = 'N/A';
            if (assetDetails?.type) {
              if (Array.isArray(assetDetails.type) && assetDetails.type.length > 0) {
                type = this.formatService.formatAssetType(assetDetails.type[0]);
              } else if (typeof assetDetails.type === 'string') {
                type = this.formatService.formatAssetType(assetDetails.type);
              }
            } else if (item.type) {
              type = Array.isArray(item.type) && item.type.length > 0
                ? this.formatService.formatAssetType(item.type[0])
                : this.formatService.formatAssetType(item.type);
            }

            const permission = assetDetails?.accessPolicy || item.accessPolicy || item.permission || 'N/A';

            let assetStatus = 'N/A';
            const statusValue = assetDetails?.itemStatus || assetDetails?.publishStatus || assetDetails?.status || item.assetStatus;
            if (statusValue) {
              assetStatus = this.formatService.toPascalCase(statusValue);
            }

            const requestStatus = this.formatService.toPascalCase(
              req.requestStatus || req.request_status || req.status || item.requestStatus || item.request_status || item.status || 'N/A'
            );

            let requestedOn = 'N/A';
            const requestDate = req.requestedAt || req.requested_at || item.requestedAt || item.requested_at || item.requestedOn || item.requested_on || item.createdAt || item.created_at || item.itemCreatedAt;
            if (requestDate) {
              requestedOn = this.formatService.formatDate(requestDate);
            }
            
            return {
              assetName,
              type,
              permission,
              assetStatus,
              requestStatus,
              requestedOn,
              action: 'Action',
              originalData: { ...item, assetDetails },
            };
          });
        
        // Use Promise.allSettled to handle partial failures gracefully
        const settledResults = await Promise.allSettled(requestsWithDetailsPromises);
        
        // Filter and map fulfilled results
        mappedData = [];
        for (const result of settledResults) {
          if (result.status === 'fulfilled') {
            mappedData.push(result.value);
          } else {
            console.warn('Request processing failed:', result.reason);
          }
        }

        // Update pagination from paginationInfo (use server totalCount so paginator shows)
        if (paginationInfo && paginationInfo['totalCount'] !== undefined) {
          this.pagination = {
            ...this.pagination,
            totalItems: paginationInfo['totalCount'],
          };
        } else {
          this.pagination = {
            ...this.pagination,
            totalItems: mappedData.length,
          };
        }
      } else if (paginationInfo && paginationInfo['totalCount'] !== undefined) {
        this.pagination = {
          ...this.pagination,
          totalItems: paginationInfo['totalCount'],
        };
      }

      this.loading = false;
      this.requestsData = [...mappedData];
      console.log('loadAssetRequests (SFTP): Data assigned - count:', mappedData.length, 'loading:', this.loading);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading SFTP asset requests:', error);
      this.requestsData = [];
      this.loading = false;
      this.cdr.detectChanges();
    }
    
    console.log('loadAssetRequests (SFTP): COMPLETE - loading is now:', this.loading, 'data count:', this.requestsData.length);
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page,
    };
    this.loadAssetRequests();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: 1,
      pageSize: pageSize,
    };
    this.loadAssetRequests();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // Assign action cell template
      const actionColumn = this.columns.find(col => col.key === 'action');
      if (actionColumn && this.actionCellTemplate) {
        actionColumn.cellTemplate = this.actionCellTemplate;
      }

      // Assign permission cell template
      const permissionColumn = this.columns.find(col => col.key === 'permission');
      if (permissionColumn && this.permissionCellTemplate) {
        permissionColumn.cellTemplate = this.permissionCellTemplate;
      }
      
      // Assign request status cell template
      const requestStatusColumn = this.columns.find(col => col.key === 'requestStatus');
      if (requestStatusColumn && this.requestStatusCellTemplate) {
        requestStatusColumn.cellTemplate = this.requestStatusCellTemplate;
      }

      // Assign asset status cell template (green/red dotted circle)
      const assetStatusColumn = this.columns.find(col => col.key === 'assetStatus');
      if (assetStatusColumn && this.assetStatusCellTemplate) {
        assetStatusColumn.cellTemplate = this.assetStatusCellTemplate;
      }
    }, 0);
  }

  getAssetStatusIndicatorClass(assetStatus: string): string {
    if (!assetStatus) return 'asset-status-indicator--na';
    const normalized = assetStatus.toLowerCase().trim();
    if (normalized === 'active') return 'asset-status-indicator--active';
    if (normalized === 'inactive') return 'asset-status-indicator--inactive';
    return 'asset-status-indicator--na';
  }

  getPermissionBadgeClass(permission: string): string {
    if (!permission) return 'permission-badge';
    const permissionUpper = permission.toUpperCase();
    if (permissionUpper === 'PUBLIC' || permissionUpper === 'OPEN') {
      return 'permission-badge permission-badge-public';
    } else if (permissionUpper === 'RESTRICTED') {
      return 'permission-badge permission-badge-restricted';
    } else if (permissionUpper === 'PRIVATE') {
      return 'permission-badge permission-badge-private';
    }
    return 'permission-badge';
  }

  getPermissionLabel(permission: string): string {
    if (!permission) return 'N/A';
    const permissionUpper = permission.toUpperCase();
    if (permissionUpper === 'OPEN') return 'Public';
    return permission.charAt(0).toUpperCase() + permission.slice(1).toLowerCase();
  }

  getRequestStatusChipClass(status: string): string {
    if (!status) return 'request-status-chip';
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus === 'granted' || normalizedStatus === 'approved') {
      return 'request-status-chip request-status-chip--granted';
    } else if (normalizedStatus === 'rejected') {
      return 'request-status-chip request-status-chip--rejected';
    } else if (normalizedStatus === 'pending') {
      return 'request-status-chip request-status-chip--pending';
    }
    return 'request-status-chip request-status-chip--default';
  }

  getRowId(row: AssetRequestData): string {
    // Use the request ID from originalData, or create a unique ID based on assetName and requestedOn
    const req = row.originalData?.assetRequest || row.originalData;
    const requestId = req?.id || row.originalData?.requestId || row.originalData?._id;
    if (requestId) {
      return String(requestId);
    }
    return `${row.assetName || 'unknown'}-${row.requestedOn || Date.now()}`;
  }

  toggleActionDropdown(event: Event, row: AssetRequestData): void {
    event.stopPropagation();
    const rowId = this.getRowId(row);
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    
    // Trigger change detection to ensure dropdown is rendered
    this.cdr.detectChanges();
    
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openDropdownId === rowId) {
      // Use a slightly longer timeout to ensure Angular has rendered the element
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        if (!button) {
          console.error('Action button not found');
          return;
        }
        
        // Try to find dropdown by searching from the button's parent wrapper
        const wrapper = button.closest('.action-cell-wrapper');
        let dropdown = wrapper?.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        
        // Fallback: search from document
        if (!dropdown) {
          dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        }
        
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.display = 'block';
          dropdown.style.visibility = 'visible';
          dropdown.style.opacity = '1';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = `${Math.max(rect.width, 150)}px`;
          dropdown.style.minWidth = `${Math.max(rect.width, 150)}px`;
          console.log('Dropdown positioned:', { top: dropdown.style.top, left: dropdown.style.left, visible: dropdown.style.visibility });
        } else {
          console.error('Dropdown not found:', { button: !!button, dropdown: !!dropdown, rowId, wrapper: !!wrapper });
        }
      }, 50);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequestData): boolean {
    const rowId = this.getRowId(row);
    return this.openDropdownId === rowId;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }

  async handleAction(action: string, row: AssetRequestData): Promise<void> {
    this.closeActionDropdown();
    
    if (action === 'view') {
      this.openViewModal(row);
    } else if (action === 'approve') {
      await this.approveRequest(row);
    } else if (action === 'reject') {
      await this.rejectRequest(row);
    }
  }

  openViewModal(row: AssetRequestData): void {
    const src = row.originalData || {};
    const req = src.assetRequest || src;
    const info = req.additionalInfo || req.additional_info || src.additionalInfo || src.additional_info || src.info || {};
    // Try to pick SFTP details from common keys; if missing, values remain blank.
    const patch = {
      sftpHost:
        info.sftpHost ||
        src.sftpHost ||
        src.host ||
        src.sftp_host ||
        src.sftp?.host ||
        '',
      port:
        info.sftpPort ||
        src.port ||
        src.sftpPort ||
        src.sftp_port ||
        src.sftp?.port ||
        '',
      username:
        info.sftpUser ||
        src.username ||
        src.userName ||
        src.user_name ||
        src.sftp?.username ||
        '',
      password:
        info.sftpPass ||
        src.password ||
        src.pass ||
        src.sftp?.password ||
        '',
      remotePath:
        info.remotePath ||
        src.remotePath ||
        src.remote_path ||
        src.sftp?.remotePath ||
        '',
      localFilePath:
        info.localPath ||
        info.localFilePath ||
        src.localFilePath ||
        src.local_file_path ||
        src.sftp?.localFilePath ||
        '',
      optionalNotes:
        info.description ||
        src.optionalNotes ||
        src.notes ||
        src.description ||
        '',
    };

    // Ensure form is disabled in view mode (per requirement)
    this.viewSftpForm.enable({ emitEvent: false });
    this.viewSftpForm.reset(patch, { emitEvent: false });
    this.viewSftpForm.disable({ emitEvent: false });
    this.isViewModalOpen = true;
    this.cdr.detectChanges();
  }

  closeViewModal(): void {
    this.isViewModalOpen = false;
  }

  async approveRequest(row: AssetRequestData): Promise<void> {
    try {
      // Get request ID from the original request item
      const req = row.originalData?.assetRequest || row.originalData;
      const requestId = req?.id || row.originalData?.requestId || row.originalData?._id;
      const assetId = req?.assetId || req?.asset_id
                     || row.originalData?.assetId || row.originalData?.asset_id
                     || row.originalData?.resourceId || row.originalData?.resource_id
                     || row.originalData?.itemId || row.originalData?.item_id
                     || row.originalData?.assetDetails?.id || row.originalData?.assetDetails?._id;
      
      if (!requestId) {
        console.error('Request ID not found in row data:', row);
        return;
      }

      if (!assetId) {
        console.error('Asset ID not found in row data:', row);
        return;
      }

      console.log('Approving request:', { requestId, assetId });

      // Step 1: Approve the asset request
      const approvePayload = {
        status: 'granted'
      };
      
      await this.authApiService.approveAssetRequest(requestId, approvePayload);
      console.log('Asset request approved successfully');

      // Step 2: Hide/Unhide the asset (make it active/visible)
      const hideUnhidePayload = {
        publishStatus: 'ACTIVE',
        dataUploadStatus: true
      };
      
      await this.catalogueApiService.hideOrUnhideItems(assetId, hideUnhidePayload);
      console.log('Asset visibility updated successfully');

      this.toasterService.success("You've successfully Approved the request");

      // Step 3: Reload the table
      await this.loadAssetRequests();
    } catch (error) {
      console.error('Error approving SFTP request:', error);
      // Error toaster is handled by networkService interceptor
    }
  }

  async rejectRequest(row: AssetRequestData): Promise<void> {
    try {
      // Get request ID from the original request item
      const req = row.originalData?.assetRequest || row.originalData;
      const requestId = req?.id || row.originalData?.requestId || row.originalData?._id;

      if (!requestId) {
        console.error('Request ID not found in row data:', row);
        return;
      }

      console.log('Rejecting request:', { requestId });

      // Reject the asset request
      const rejectPayload = {
        status: 'rejected'
      };
      
      await this.authApiService.approveAssetRequest(requestId, rejectPayload);
      console.log('Asset request rejected successfully');

      this.toasterService.error("You've successfully Rejected the request");

      // Reload the table
      await this.loadAssetRequests();
    } catch (error) {
      console.error('Error rejecting SFTP request:', error);
      // Error toaster is handled by networkService interceptor
    }
  }
}
